"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react";
import type { BalanceInformation } from "../services/helpers/balanceUtils";

export interface BalanceConfirmationPrefillData {
  /** Balance confirmation request id; used for source_reference when creating ledger recon job */
  balanceConfirmationId?: string;
  /** Customer id from balance confirmation; used for S3 key generation */
  customerId?: string;
  /** Generic-recon entity id, sourced from the balance-confirmation counterparty. */
  entityId?: string;
  entityName: string;
  startDate: string;
  endDate: string;
  perspective: string;
  type: string;
  instructions?: string;
  balances?: BalanceInformation;
  yourFilenames?: string[];
  counterPartyFilenames?: string[];
}

export interface BalanceConfirmationHandoffValue {
  openNewReconModal: boolean;
  prefillData: BalanceConfirmationPrefillData | null;
}

interface BalanceConfirmationHandoffContextType {
  handoffData: BalanceConfirmationHandoffValue;
  setHandoffData: (value: BalanceConfirmationHandoffValue) => void;
  clearHandoffData: () => void;
}

const initialValue: BalanceConfirmationHandoffValue = {
  openNewReconModal: false,
  prefillData: null,
};

const BalanceConfirmationHandoffContext =
  createContext<BalanceConfirmationHandoffContextType | undefined>(undefined);

export function BalanceConfirmationHandoffProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [handoffData, setHandoffDataState] =
    useState<BalanceConfirmationHandoffValue>(initialValue);

  const setHandoffData = useCallback((value: BalanceConfirmationHandoffValue) => {
    setHandoffDataState(value);
  }, []);

  const clearHandoffData = useCallback(() => {
    setHandoffDataState(initialValue);
  }, []);

  return (
    <BalanceConfirmationHandoffContext.Provider
      value={{ handoffData, setHandoffData, clearHandoffData }}
    >
      {children}
    </BalanceConfirmationHandoffContext.Provider>
  );
}

export function useBalanceConfirmationHandoff() {
  const context = useContext(BalanceConfirmationHandoffContext);
  if (context === undefined) {
    throw new Error(
      "useBalanceConfirmationHandoff must be used within a BalanceConfirmationHandoffProvider"
    );
  }
  return context;
}
